import { useCallback } from 'react';

import { useAppStore } from '@/store';
import submitWebHook from '@/requests/magic-flow/submitWebHook';
import createNotificationConfig from '@/utils/ui/createNotificationConfig';
import buildParamsFromInputs from '@/utils/flow/buildParamsFromInputs';

import type { IAppStore } from '@/store';

export interface SubmitResult {
  id: string;
}

const selector = (store: IAppStore) => ({
  toolId: store.toolId,
  folderName: store.folderName,
  inputs: store.inputs,
  selectedPreset: store.selectedPreset,
});

/**
 * Hook for handling webhook submission. Returns { id } from the SDK webhook
 * endpoint; the id is consumed locally by the polling pipeline rather than
 * stored globally.
 */
const useGenerationSubmit = () => {
  const { toolId, folderName, inputs, selectedPreset } = useAppStore(selector);

  const submit = useCallback(
    async (
      flowId: string,
      prebuiltParams?: Record<string, unknown>
    ): Promise<SubmitResult> => {
      const params = prebuiltParams ?? buildParamsFromInputs(inputs);
      const presetSlug = selectedPreset?.slug;
      const presetCategory = selectedPreset?.category;

      const { id } = await submitWebHook({
        webhookId: flowId,
        params,
        driveOptions: {
          folderName,
          packageId: toolId,
          // Backend workaround: the backend doesn't accept a separate `slug` parameter — adding an
          // extra parameter causes generations not to be saved into drive — so we smuggle the slug inside the
          // existing `category` field.
          ...(presetSlug && {
            attributes: {
              category: `slug:${presetSlug}, category:${presetCategory || ''}`,
            },
          }),
        },
        notificationConfig: createNotificationConfig(),
      });

      return { id };
    },
    [inputs, folderName, toolId, selectedPreset]
  );

  return { submit };
};

export default useGenerationSubmit;
